// src/pages/kuyumcu/KuyumcuStokIslemPage.jsx

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import KuyumcuLeftSideNavbar from "./components/KuyumcuLeftSideNavbar";
import PremiumBackground from "./components/PremiumBackground";

import ProsetBackButton from "./components/ProsetBackButton";
import ProsetPopup from "./components/ProsetPopup";
import SilmeUyariPopup from "./components/SilmeUyariPopup";
import { formatNumberTR } from "./utils/numberFormat";
import { usePermission } from "../../context/PermissionContext";

import {
  Box,
  Button,
  TextField,
  MenuItem,
  Collapse,
} from "@mui/material";

import axiosInstance from "../../api/axiosInstance";
import { getUserId } from "../../utils/getUserId";

import "./css/KuyumcuMainPage.css";
import "./css/KuyumcuStokPage.css";
import "./css/KuyumcuStokIslemPage.css";

// ----------------------------------------------------------
// İşlem türü label
// ----------------------------------------------------------
const formatIslemTurLabel = (tur) => {
  if (!tur) return "";
  switch (tur) {
    case "STOK_GIRIS": return "Stok Giriş";
    case "STOK_CIKIS": return "Stok Çıkış";
    case "ZIYNET_ALINDI": return "Ziynet Alındı";
    case "ZIYNET_VERILDI": return "Ziynet Verildi";
    case "ALTIN_ALINDI": return "Altın Alındı";
    case "ALTIN_VERILDI": return "Altın Verildi";
    default: return tur;
  }
};

// Tarih gösterimi
const formatFullDateTime = (isoStr) => {
  if (!isoStr) return "";

  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return "";

  const gun = String(d.getDate()).padStart(2, "0");
  const ay = String(d.getMonth() + 1).padStart(2, "0");
  const yil = d.getFullYear();

  const saat = String(d.getHours()).padStart(2, "0");
  const dakika = String(d.getMinutes()).padStart(2, "0");

  return `${gun}.${ay}.${yil} ${saat}:${dakika}`;
};


const KuyumcuStokIslemPage = () => {
  const { stokId } = useParams();
  const navigate = useNavigate();

  // ✔ Güvenli userId
  const userId = getUserId();
  const { can, isLoading } = usePermission();

  // ====== STOK İŞLEM SAYFASI YETKİLERİ (DB'DEKİ KODLARLA EŞLEŞTİR) ======
  const PERM_STOK_ISLEM_VIEW = "STOK_ISLEMLER_VIEW";
  const PERM_STOK_ISLEM_CREATE = "STOK_ISLEMLER_CREATE";
  const PERM_STOK_ISLEM_UPDATE = "STOK_ISLEMLER_UPDATE";
  const PERM_STOK_ISLEM_DELETE = "STOK_ISLEMLER_DELETE";

  // Sayfa içinde kolay kullanım
  const canView = !isLoading && can(PERM_STOK_ISLEM_VIEW);
  const canCreate = !isLoading && can(PERM_STOK_ISLEM_CREATE);
  const canUpdate = !isLoading && can(PERM_STOK_ISLEM_UPDATE);
  const canDelete = !isLoading && can(PERM_STOK_ISLEM_DELETE);



  const [stok, setStok] = useState(null);
  const [rows, setRows] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [silmePopupOpen, setSilmePopupOpen] = useState(false);
  const [silinecekIslem, setSilinecekIslem] = useState(null);

  const [form, setForm] = useState({
    id: null,
    stokId: Number(stokId),
    stokAdi: "",
    hesapId: null,
    hesapAdi: "",
    tarih: "",
    islemTur: "STOK_GIRIS",
    birim: "",
    miktar: "",
    milyem: "",
    hasGram: "",
    adet: "",
    iscilik: "",
    iscilikGram: "",
    aciklama: "",
  });

  // ----------------------------------------------------------
  // Giriş kontrolü
  // ----------------------------------------------------------
  useEffect(() => {
    if (!userId) {
      navigate("/login");
      return;
    }
  }, [userId]);

  // ----------------------------------------------------------
  // Stok bilgisi
  // ----------------------------------------------------------
  const loadStok = async () => {
    if (!userId) return;

    try {
      const resp = await axiosInstance.get(`/stoklar?userId=${userId}`);
      const s = resp.data.find((x) => x.id === Number(stokId));
      setStok(s || null);
    } catch (e) {
      console.error("Stok bilgisi alınamadı:", e);
      alert("Stok bilgisi alınırken hata oluştu.");
    }
  };

  // ----------------------------------------------------------
  // Stok işlemleri listesi
  // ----------------------------------------------------------
  const loadIslemler = async () => {
    if (!userId) return;

//     if (isLoading) return;
//     if (!can(PERM_STOK_ISLEM_VIEW)) {
//       setRows([]);
//       return;
//     }


    try {
      const resp = await axiosInstance.get(`/stok-islem?stokId=${stokId}`);
      let list = resp.data || [];

      if (filterStart) list = list.filter((x) => x.tarih >= filterStart);
      if (filterEnd)
        list = list.filter(
          (x) => x.tarih && x.tarih <= filterEnd + "T23:59:59"
        );

      list.sort((a, b) => new Date(b.tarih) - new Date(a.tarih));
      setRows(list);
    } catch (e) {
      console.error("Stok işlemleri alınamadı:", e);
      alert("Stok işlemleri alınırken hata oluştu.");
    }
  };

  useEffect(() => {
    loadStok();
  }, [userId]);

  useEffect(() => {
    if (stokId) loadIslemler();
  }, [stokId, filterStart, filterEnd, userId, isLoading]);


  // ----------------------------------------------------------
  // Yeni işlem popup reset
  // ----------------------------------------------------------
  const resetForm = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    const currentDateTime = now.toISOString().slice(0, 16);

    setForm({
      id: null,
      stokId: Number(stokId),
      stokAdi: stok?.stokAdi || "",
      hesapId: null,
      hesapAdi: "",
      tarih: currentDateTime,
      islemTur: "STOK_GIRIS",
      birim: stok?.birim || "",
      miktar: "",
      milyem: "",
      hasGram: "",
      adet: "",
      iscilik: "",
      iscilikGram: "",
      aciklama: "",
    });
  };

  const handleOpenNew = () => {
    if (isLoading || !can(PERM_STOK_ISLEM_CREATE)) {
      alert("Yeni stok işlemi ekleme yetkiniz yok.");
      return;
    }
    setEditMode(false);
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    if (isLoading || !can(PERM_STOK_ISLEM_UPDATE)) {
      alert("Stok işlemi düzenleme yetkiniz yok.");
      return;
    }

    if (row.islemTur !== "STOK_GIRIS" && row.islemTur !== "STOK_CIKIS") return;

    setEditMode(true);
    setForm({
      id: row.id,
      stokId: row.stokId,
      stokAdi: row.stokAdi,
      hesapId: row.hesapId || null,
      hesapAdi: row.hesapAdi || "",
      tarih: row.tarih ? row.tarih.slice(0, 16) : "",
      islemTur: row.islemTur,
      birim: row.birim || stok?.birim || "",
      miktar: row.miktar ?? "",
      milyem: row.milyem ?? "",
      hasGram: row.hasGram ?? "",
      adet: row.adet ?? "",
      iscilik: row.iscilik ?? "",
      iscilikGram: row.iscilikGram ?? "",
      aciklama: row.aciklama ?? "",
    });

    setDialogOpen(true);
  };

  // ----------------------------------------------------------
  // Kaydet / Güncelle
  // ----------------------------------------------------------
  const handleSave = async () => {

    if (isLoading) return;

    const needPerm = editMode ? PERM_STOK_ISLEM_UPDATE : PERM_STOK_ISLEM_CREATE;
    if (!can(needPerm)) {
      alert("Bu işlem için yetkiniz yok.");
      return;
    }

    if (!form.tarih || !form.islemTur) {
      alert("Tarih ve işlem türü zorunludur.");
      return;
    }

    if (stok.birim === "ADET" && !form.miktar) {
      alert("Adet zorunludur.");
      return;
    }

    if (stok.birim === "GRAM" && !form.hasGram) {
      alert("Has gram zorunludur.");
      return;
    }


    if (form.islemTur !== "STOK_GIRIS" && form.islemTur !== "STOK_CIKIS") {
      alert("Bu ekranda sadece Stok Giriş / Stok Çıkış kullanılabilir.");
      return;
    }

    const payload = {
      id: form.id,
      ustIslemId: null,
      stokId: form.stokId,
      stokAdi: form.stokAdi || stok?.stokAdi,
      hesapId: form.hesapId,
      kullaniciId: userId, // ✔ güvenli
      tarih: form.tarih,
      islemTur: form.islemTur,
      birim: form.birim || stok?.birim,

      miktar:
          stok.birim === "ADET"
            ? Number(form.miktar)
            : null,

      hasGram:
          stok.birim === "GRAM"
            ? Number(form.hasGram)
            : null,

      aciklama: form.aciklama || "",
      milyem: form.milyem ? Number(form.milyem) : null,
      adet: form.adet ? Number(form.adet) : null,
      iscilik: form.iscilik ? Number(form.iscilik) : null,
      iscilikGram: form.iscilikGram ? Number(form.iscilikGram) : null,
    };

    try {
      if (editMode && form.id) {
        await axiosInstance.put("/stok-islem", payload);
      } else {
        await axiosInstance.post("/stok-islem", payload);
      }

      setDialogOpen(false);
      await loadIslemler();
      await loadStok();
    } catch (e) {
      console.error("Stok işlemi kaydedilemedi:", e);
      alert("Kayıt sırasında hata oluştu.");
    }
  };

  // ----------------------------------------------------------
  // Silme
  // ----------------------------------------------------------
  const handleDeleteClick = (row) => {
    if (row.islemTur !== "STOK_GIRIS" && row.islemTur !== "STOK_CIKIS") return;

    setSilinecekIslem(row);
    setSilmePopupOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (isLoading || !can(PERM_STOK_ISLEM_DELETE)) {
      alert("Silme yetkiniz yok.");
      setSilmePopupOpen(false);
      setSilinecekIslem(null);
      return;
    }

    if (!silinecekIslem) return;

    try {
      await axiosInstance.delete(`/stok-islem/${silinecekIslem.id}`);
      await loadIslemler();
      await loadStok();
    } catch (e) {
      console.error("Stok hareketi silinemedi:", e);
      alert("Silme sırasında hata oluştu.");
    } finally {
      setSilmePopupOpen(false);
      setSilinecekIslem(null);
    }
  };



  // ----------------------------------------------------------
  // Detay aç/kapat
  // ----------------------------------------------------------
  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // ----------------------------------------------------------
  // Yükleniyor ekranı
  // ----------------------------------------------------------
  if (!stok) {
    return (
      <div className="main-container">
        <KuyumcuLeftSideNavbar />
        <div className="stok-content">
          <ProsetBackButton label="Geri Dön" />
          <p>Stok bilgisi yükleniyor...</p>
        </div>
      </div>
    );
  }

  const filterActive = filterStart !== "" || filterEnd !== "";


  // ----------------------------------------------------------
  // UI
  // ----------------------------------------------------------
  return (
    <div className="main-container">
      <PremiumBackground />
      <KuyumcuLeftSideNavbar />

      <div className="stok-content">
        <ProsetBackButton label="Geri Dön" />

        <h1>
          {stok.stokAdi}{" "}
          <span style={{ fontSize: "1rem", color: "#555", marginLeft: 8 }}>
            {stok.birim === "ADET"
              ? Math.round(stok.miktar).toLocaleString("tr-TR")
              : Number(stok.miktar).toLocaleString("tr-TR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
            }{" "}
            {stok.birim}
          </span>
        </h1>

        {canCreate && (
          <Button
            variant="contained"
            sx={{
              background: "#b8860b",
              color: "#fff",
              "&:hover": { background: "#d4a017" },
              mb: 2,
            }}
            onClick={handleOpenNew}
          >
            Yeni Stok Giriş / Çıkış
          </Button>
        )}

        {/* FİLTRELER */}
        <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
          <TextField
            type="date"
            label="Başlangıç"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filterStart}
            onChange={(e) => setFilterStart(e.target.value)}
          />
          <TextField
            type="date"
            label="Bitiş"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filterEnd}
            onChange={(e) => setFilterEnd(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={() => {
              setFilterStart("");
              setFilterEnd("");
            }}
            sx={{
              background: filterActive
                ? "linear-gradient(135deg, #ffb3b3, #e66767)"    // 🔴 Filtre aktifken (kırmızı-altın)
                : "linear-gradient(135deg, #f5d68a, #d7a441)",   // 🟡 Filtre yokken (altın)
              color: filterActive ? "#7a0d0d" : "#4a3305",
              fontWeight: 600,
              padding: "7px 16px",
              borderRadius: "10px",
              textTransform: "none",
              boxShadow: filterActive
                ? "0 2px 6px rgba(200,0,0,0.35)"
                : "0 2px 6px rgba(184,134,11,0.35)",
              transition: "all .25s ease",
              "&:hover": {
                background: filterActive
                  ? "linear-gradient(135deg, #ffcccc, #cc4d4d)"   // kırmızı daha parlak
                  : "linear-gradient(135deg, #ffdf8d, #c58a2d)",  // altın daha parlak
                boxShadow: filterActive
                  ? "0 4px 12px rgba(200,0,0,0.45)"
                  : "0 4px 12px rgba(184,134,11,0.45)",
                transform: "translateY(-2px)",
              },
            }}
          >
            {filterActive ? "Tarih Filtre Temizle" : "Tüm İşlemler"}
          </Button>



        </Box>

        {/* LİSTE */}
        <div className="mobile-list">
          {rows.map((r) => {
            const isStokIslemi =
              r.islemTur === "STOK_GIRIS" || r.islemTur === "STOK_CIKIS";

            return (
              <div className="mobile-card" key={r.id}>

                {/* HEADER */}
                <div className="mc-header">
                  <span className="mc-title">{formatFullDateTime(r.tarih)}</span>

                  <span
                    className={`mc-tag ${
                      r.islemTur === "STOK_GIRIS" || r.islemTur === "STOK_CIKIS"
                        ? "stok"
                        : r.islemTur?.endsWith("ALINDI")
                        ? "alindi"
                        : r.islemTur?.endsWith("VERILDI")
                        ? "verildi"
                        : ""
                    }`}
                  >
                    {formatIslemTurLabel(r.islemTur)}
                  </span>


                </div>

                {/* KART GÖVDE — SOL + SAĞ */}
                <div className="mc-split">

                  {/* SOL TARAF */}
                  <div className="mc-left">
{/*                     <div className="mc-row"> */}
{/*                       <label>Tarih / Saat:</label> */}
{/*                         <span>{formatFullDateTime(r.tarih)}</span> */}
{/*                     </div> */}
                     <div className="mc-row">
                           <label>Miktar:</label>
                           <span>
                             {formatNumberTR(
                               r.miktar,
                               r.birim === "ADET" ? 0 : 2
                             )}
                           </span>
                         </div>

                         <div className="mc-row">
                           <label>Milyem:</label>
                           <span>
                             {formatNumberTR(r.milyem, 0)}
                           </span>
                         </div>

                         <div className="mc-row">
                           <label>Has Gram:</label>
                           <span>
                             {formatNumberTR(r.hasGram, 2)}
                           </span>
                         </div>

                         <div className="mc-row">
                           <label>Birim:</label>
                           <span>{r.birim || "-"}</span>
                         </div>

                         {r.aciklama && (
                           <div className="mc-row">
                             <label>Açıklama:</label>
                             <span>{r.aciklama}</span>
                           </div>
                         )}
                       </div>

                       {/* SAĞ TARAF */}
                       <div className="mc-right">

                         <div className="mc-row">
                           <label>Hesap Adı:</label>
                           <span>{r.hesapAdi || "-"}</span>
                         </div>

                         <div className="mc-row">
                           <label>Adet:</label>
                           <span>
                             {formatNumberTR(r.adet, 0)}
                           </span>
                         </div>

                         <div className="mc-row">
                           <label>İşçilik:</label>
                           <span>
                             {formatNumberTR(r.iscilik, 2)}
                           </span>
                         </div>

                         <div className="mc-row">
                           <label>İşçilik Gr.:</label>
                           <span>
                             {formatNumberTR(r.iscilikGram, 2)}
                           </span>
                         </div>

                       </div>
                </div>

                {/* BUTONLAR */}
                <div className="mc-actions-bottom">
                  {isStokIslemi && (
                    <>
                      {canUpdate && (
                        <button
                          className="edit-btn gold-btn"
                          onClick={() => handleOpenEdit(r)}
                        >
                          📑 Düzenle
                        </button>
                      )}

                      {canDelete && (
                        <button
                          className="delete-btn gold-btn red-btn"
                          onClick={() => handleDeleteClick(r)}
                        >
                          🗑 Sil
                        </button>
                      )}
                    </>
                  )}
                </div>


                </div>

            );
          })}

          {rows.length === 0 && (
            <p style={{ marginTop: 16, color: "#777" }}>
              Bu stoğa ait stok hareketi bulunmuyor.
            </p>
          )}
        </div>


        {/* POPUP */}
        <ProsetPopup
          open={dialogOpen}
          title={editMode ? "Stok İşlemi Düzenle" : "Yeni Stok Giriş / Çıkış"}
          editMode={editMode}
          sabitMi={false}
          onSave={handleSave}
          onCancel={() => setDialogOpen(false)}
          onDelete={
            editMode && canDelete
              ? () => handleDeleteClick(form)
              : undefined
          }

        >
          <TextField
            fullWidth
            margin="dense"
            type="datetime-local"
            label="Tarih"
            InputLabelProps={{ shrink: true }}
            value={form.tarih}
            onChange={(e) => setForm({ ...form, tarih: e.target.value })}
          />

          <TextField
            fullWidth
            margin="dense"
            select
            label="İşlem Türü"
            value={form.islemTur}
            onChange={(e) => setForm({ ...form, islemTur: e.target.value })}
          >
            <MenuItem value="STOK_GIRIS">Stok Giriş</MenuItem>
            <MenuItem value="STOK_CIKIS">Stok Çıkış</MenuItem>
          </TextField>

          {/* ADET STOK → MİKTAR */}
          {stok.birim === "ADET" && (
            <TextField
              fullWidth
              margin="dense"
              label="Adet"
              type="number"
              value={form.miktar}
              onChange={(e) =>
                setForm({ ...form, miktar: e.target.value, hasGram: "" })
              }
            />
          )}

          {/* GRAM STOK → HAS GRAM */}
          {stok.birim === "GRAM" && (
            <TextField
              fullWidth
              margin="dense"
              label="Has Gram"
              type="number"
              inputProps={{ step: "0.01" }}
              value={form.hasGram}
              onChange={(e) =>
                setForm({ ...form, hasGram: e.target.value, miktar: "" })
              }
            />
          )}


          <TextField
            fullWidth
            margin="dense"
            label="Açıklama"
            value={form.aciklama}
            onChange={(e) => setForm({ ...form, aciklama: e.target.value })}
          />
        </ProsetPopup>

        <SilmeUyariPopup
          open={silmePopupOpen}
          message="Bu stok hareketini silmek istediğinize emin misiniz?"
          onCancel={() => {
            setSilmePopupOpen(false);
            setSilinecekIslem(null);
          }}
          onConfirm={handleDeleteConfirmed}
        />

      </div>
    </div>
  );
};

export default KuyumcuStokIslemPage;
